/**
 * WorkspaceHome — Homepage with Hero + scenario card grid.
 *
 * Clicking the Hero CTA or any card prefills the chat input without sending,
 * letting the user review and decide when to submit.
 */

import { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useChatPanel } from '../../context/ChatPanelContext';
import { useImpersonation } from '../../context/ImpersonationContext';
import './WorkspaceHome.css';

// ── Corner links ────────────────────────────────────────────────────────────

/** SharePoint doc of ready-to-copy, scenario-organised prompts. Opens in a new
 *  tab, same as the agent selection guide link beside it — neither downloads. */
const PROMPT_COLLECTION_URL =
  'https://amazon-my.sharepoint.com/:u:/p/qiuyac/IQBSHOKNWDBSTrI_h44L0q58AUCvu5HtJihDDN87fbnodOU?e=1QTgjV';

// ── Hero CTA prompt ─────────────────────────────────────────────────────────

const HERO_PROMPT = `检查是否已装备 sales-daily-brief skill，若有则执行该 skill 生成今日简报。若无，则手动生成我的每日销售简报，包含：
最近一周逐日usage及revenue明细，
1. 账号收入异常，
2. 需跟进的pipeline，
3. 近期我的top客户的新闻与我所属行业动态，
4. 今日 Top 3 优先事项。
格式：简洁要点。`;

// ── Card data ───────────────────────────────────────────────────────────────

export interface WorkspaceCard {
  id: string;
  title: string;
  description: string;
  buttonText: string;
  icon: string;
  prompt?: string;
  action?: 'chat' | 'navigate';
  href?: string;
  /** Renders a "New" badge beside the title, to draw the eye to a fresh card. */
  isNew?: boolean;
}

const DEFAULT_CARDS: WorkspaceCard[] = [
  {
    id: 'target-attainment',
    title: '我的 quota 能完成吗？',
    description: '看看缺口多大，还需要补多少 pipeline。',
    buttonText: '分析我的 Forecast →',
    icon: '/icons/target-attainment.svg',
    prompt: `执行 Baseline Forecast Report skill，生成报告后给我管理层摘要：
1. 按季度及按年的达成率定位（金额+百分比），
2. 最值得推进的 3-5 个 deal，
3. 最大的结构性风险等，
4. 用户及其下级自己填写forecast时的填写内容可酌情参考。
Skill 可用时优先执行；不可用时基于可查询数据完成等效分析。`,
  },
  {
    id: 'ivt-solution',
    title: '哪些 IVT Solution 能为我创造新机会？',
    description: '为我的客户找到最匹配的 IVT Solution，用已有资产创造新的销售机会。',
    buttonText: '匹配 IVT Asset →',
    icon: '/icons/daily-briefing.svg',
    isNew: true,
    prompt: `帮我找出我territory下最适合推进IVT Asset的客户（每个客户最多3个Asset）。

先查我的territory全量客户和行业分布，从IVT全量Asset中匹配：
- 首要看Asset与客户业务场景的匹配度，只保留有明确匹配的客户
- 匹配度相近时，按open opp数+已落地数+scoreX综合排序
- 优先本行业Asset，跨行业适配度明显更高时补充推荐
- 查询客户现有opp和revenue，估算每个推荐可撬动的ARR增量

先输出Executive Summary（最多3条）：①最应优先推进的客户+Asset组合+预估可撬动ARR ②本周该联系的IVT联系人及具体诉求 ③还有哪几个客户值得跟进。

再输出明细表格：客户名/推荐Asset/🏠本行业or🔀跨行业/Asset描述/💡为什么适合(结合该客户具体opp、revenue、业务特征)/💰预估ARR/🎯下一步动作/📧IVT联系人。用🟢🟡🔴标注置信度，匹配度优先、同等匹配度按客户重要性排序。

IVT联系人：AUTO+MFG+MEAD→yingyn@|RCH+FSI+HCLS+GAMES→chuaamz@|EDU→viviannw@|LOG→xiujason@|ENE→hjinamzn@`,
  },
  {
    id: 'latest-news',
    title: '我的客户有什么新动态？',
    description: '新闻、财报、采购信号一次看完。',
    buttonText: '查看客户情报 →',
    icon: '/icons/latest-news.svg',
    prompt: `查询我负责客户和所在行业的最新外部动态（新闻、公告、行业趋势），生成情报简报。先判断用户是否属于GAMES(L4), 如果是，执行 daily sales intelligence brief skill 后提炼：
- 客户动态 — 融资/并购/高管变动/新产品发布/财报/裁员等重大事件
- 行业信号 — 政策变化/技术趋势/竞对动作中与我的客户相关的
- 每条标注：🟢 商机信号 / 🟡 值得关注 / 🔴 风险预警
- 每条附：为什么跟我有关 + 建议的跟进动作或对话切入点
聚焦过去 7 天，按业务相关性排序。只呈现与我的客户或行业直接相关的信息，不要泛泛的行业综述。
用户符合条件且Skill 可用时优先执行skill；用户不符合条件或者skill不可用时进行等效分析。`,
  },
  {
    id: 'pipeline-hygiene',
    title: '哪些 deal 正在变冷？',
    description: '找出停滞商机，越早挽回越好。',
    buttonText: '检查 Pipeline 健康度 →',
    icon: '/icons/code-buz.svg',
    prompt: `评估我的 Pipeline 健康度。检查过期未关单、阶段卡顿、长期无更新、漏斗形状异常等。
输出：整体情况评估， 问题商机清单（带SFDC链接），优先处理建议等。`,
  },
  {
    id: 'latest-report',
    title: '本周收入表现如何？',
    description: 'Revenue 趋势、异常、Top 增长/下降客户。',
    buttonText: '生成周报摘要 →',
    icon: '/icons/company-analysis.svg',
    prompt: `执行 Weekly Revenue Report（我的 territory，上一完整周），生成报告后给执行摘要：
1. 一句话定位整体走势，
2. WoW 变化最显著的 Top 3 客户及原因，
3. 需介入的风险客户和扩展机会，
4. 2-3 条优先行动建议。
Skill 可用时优先执行；不可用时基于可查询数据完成等效分析。`,
  },
  {
    id: 'cheetah',
    title: '预约专家',
    description: '一键预约 / 回访 China Tech 团队专家，提升客户对接效率。',
    buttonText: '进入 Cheetah →',
    icon: '/icons/customer-deep.svg',
    action: 'navigate',
    href: '/cheetah',
  },
];

// ── Component ───────────────────────────────────────────────────────────────

interface WorkspaceHomeProps {
  cards?: WorkspaceCard[];
}

const BUSY_TIPS = [
  '☕ Rocky 正在思考中，喝口茶稍等一下~',
  '🧠 Rocky 还在忙，让它先回完再说~',
  '⏳ 等 Rocky 回复完再点，别急~',
  '🍵 Rocky 专注中，稍后再来~',
];

export default function LegacyHome({ cards = DEFAULT_CARDS }: WorkspaceHomeProps) {
  const { sendPrompt, chatQueueFull } = useChatPanel();
  const { isImpersonating } = useImpersonation();
  const navigate = useNavigate();
  const [busyToast, setBusyToast] = useState<string | null>(null);
  const busyToastTimer = useRef<ReturnType<typeof setTimeout>>();

  const showBusyToast = useCallback((msg?: string) => {
    const tip = msg ?? BUSY_TIPS[Math.floor(Math.random() * BUSY_TIPS.length)];
    clearTimeout(busyToastTimer.current);
    setBusyToast(tip);
    busyToastTimer.current = setTimeout(() => setBusyToast(null), 2500);
  }, []);

  const flyToInput = useCallback((e: React.SyntheticEvent<HTMLElement>, prompt: string, variant: 'card' | 'hero' = 'card') => {
    if (isImpersonating) {
      showBusyToast('👀 当前为 Admin 查看模式，无法操作');
      return;
    }
    // Refuse before any animation if the queue is at capacity, so we
    // don't fly the ghost into the input only to have ChatPanel reject it.
    if (chatQueueFull) {
      showBusyToast('待发送队列最多 3 条');
      return;
    }

    const el = e.currentTarget;
    const rect = el.getBoundingClientRect();

    // Create ghost immediately from the clicked element
    const ghost = document.createElement('div');
    ghost.className = variant === 'hero' ? 'ws-home__hero-ghost' : 'ws-home__card-ghost';
    ghost.style.width = `${rect.width}px`;
    ghost.style.height = `${rect.height}px`;
    ghost.style.left = `${rect.left}px`;
    ghost.style.top = `${rect.top}px`;
    ghost.textContent = variant === 'hero' ? el.textContent : '';
    if (variant === 'card') {
      Array.from(el.childNodes).forEach(child => ghost.appendChild(child.cloneNode(true)));
    }
    document.body.appendChild(ghost);

    const glowClass = variant === 'hero' ? 'chat-panel__textarea--glow-teal' : 'chat-panel__textarea--glow';

    const animateGhost = (textarea: Element) => {
      const targetRect = textarea.getBoundingClientRect();
      const targetX = targetRect.left + targetRect.width / 2 - rect.width / 2;
      const targetY = targetRect.top + targetRect.height / 2 - rect.height / 2;
      const dx = targetX - rect.left;
      const dy = targetY - rect.top;

      requestAnimationFrame(() => {
        ghost.style.transform = `translate(${dx}px, ${dy}px) scale(0.15)`;
        ghost.style.opacity = '0';
      });

      const cleanup = setTimeout(() => ghost.remove(), 1000);
      ghost.addEventListener('transitionend', () => {
        clearTimeout(cleanup);
        ghost.remove();
        textarea.classList.add(glowClass);
        setTimeout(() => textarea.classList.remove(glowClass), 600);
      }, { once: true });
    };

    // Send directly: handleSend auto-sends when idle, enqueues when streaming.
    sendPrompt(prompt);

    // If textarea is already visible, animate immediately
    const textarea = document.querySelector('.chat-panel__textarea');
    if (textarea && textarea.getBoundingClientRect().width > 0) {
      animateGhost(textarea);
    } else {
      // Panel was collapsed — wait for textarea to appear in the DOM
      const observer = new MutationObserver(() => {
        const ta = document.querySelector('.chat-panel__textarea');
        if (ta && ta.getBoundingClientRect().width > 0) {
          observer.disconnect();
          clearTimeout(safetyTimer);
          animateGhost(ta);
        }
      });
      observer.observe(document.body, { childList: true, subtree: true });
      const safetyTimer = setTimeout(() => { observer.disconnect(); ghost.remove(); }, 1000);
    }
  }, [sendPrompt, chatQueueFull, isImpersonating, showBusyToast]);

  return (
    <div className="ws-home">
      <a
        className="ws-home__corner-link ws-home__prompts-link"
        href={PROMPT_COLLECTION_URL}
        target="_blank"
        rel="noopener noreferrer"
      >
        Rocky按情景Prompt合集 · 复制就能用
      </a>
      <a
        className="ws-home__corner-link ws-home__guide-link"
        href="/docs/CMHK-AI-Agent-Guide.html"
        target="_blank"
        rel="noopener noreferrer"
      >
        CMHK AI Agents 选用指南
      </a>
      {/* Hero */}
      <section className="ws-home__hero">
        <h1 className="ws-home__title">
          <span className="ws-home__title-highlight">Rocky,</span> 你的随身军师
        </h1>
        <p className="ws-home__subtitle">每天看一眼，上场从容，心里有数</p>
        <button
          className="ws-home__hero-cta"
          onClick={(e) => flyToInput(e, HERO_PROMPT, 'hero')}
        >
          一键获取今日作战简报
        </button>
      </section>

      {/* Cards */}
      <div className="ws-home__grid">
        {cards.map(card => {
          const handleClick = (e: React.SyntheticEvent<HTMLElement>) => {
            if (card.action === 'navigate' && card.href) {
              navigate(card.href);
              return;
            }
            if (card.prompt) flyToInput(e, card.prompt);
          };
          return (
            <div
              key={card.id}
              className="ws-home__card"
              onClick={handleClick}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleClick(e); } }}
            >
              <div className="ws-home__card-icon">
                <img src={card.icon} alt={card.title} width="48" height="48" />
              </div>
              {card.isNew && <span className="ws-home__card-badge">NEW</span>}
              <h2 className="ws-home__card-title">{card.title}</h2>
              <p className="ws-home__card-desc">{card.description}</p>
            </div>
          );
        })}
      </div>

      {/* Busy toast */}
      {busyToast && (
        <div className="ws-home__busy-toast">{busyToast}</div>
      )}
    </div>
  );
}
